<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../../SettingsLayout.vue';
import KanbanBoardModal from './KanbanBoardModal.vue';

const store = useStore();
const { t } = useI18n();

const teams = computed(() => store.getters['teams/getTeams'] || []);
const board = computed(() => store.getters['boards/getBoard']);
const boards = computed(() => store.getters['boards/getBoards'] || []);
const ui = computed(() => store.getters['boards/getUIFlags']);

const selectedTeamId = ref(null);
const boardName = ref('');
const columns = ref([{ name: '' }, { name: '' }, { name: '' }]);
const showCreate = ref(false);
const showEdit = ref(false);
const selectedBoard = ref(null);

const loadBoard = async teamId => {
  await store.dispatch('boards/getByTeam', teamId);
  if (board.value) {
    boardName.value = board.value.name;
    columns.value = (board.value.board_columns || []).map(c => ({
      id: c.id,
      name: c.name,
      position: c.position,
    }));
  } else {
    boardName.value = '';
    columns.value = [{ name: '' }, { name: '' }, { name: '' }];
  }
};

onMounted(async () => {
  await store.dispatch('teams/get');
  await store.dispatch('boards/getAll');
});

const getTeamName = teamId => {
  const team = teams.value.find(t => t.id === teamId);
  const name = team ? team.name : `Time #${teamId}`;
  return name.length > 25 ? `${name.slice(0, 25)}...` : name;
};

const selectForEdit = async b => {
  selectedTeamId.value = b.team_id;
  await loadBoard(b.team_id);
  selectedBoard.value = b;
  showEdit.value = true;
};

const removeBoard = async id => {
  try {
    await store.dispatch('boards/delete', id);
    if (selectedTeamId.value) {
      await loadBoard(selectedTeamId.value);
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      'Erro ao remover o quadro. Tente novamente.';
    useAlert(errorMessage, { duration: 6000 });
  }
};
</script>

<template>
  <div class="flex items-start w-full max-w-6xl mx-auto p-6">
    <SettingsLayout
      :is-loading="ui.isFetching"
      :no-records-found="!boards.length"
      no-records-message="Nenhum quadro configurado."
    >
      <template #header>
        <BaseSettingsHeader
          title="Quadro"
          description="Configure um quadro por time e gerencie suas colunas"
        >
          <template #actions>
            <Button
              icon="i-lucide-circle-plus"
              label="Novo quadro"
              @click="showCreate = true"
            />
          </template>
        </BaseSettingsHeader>
      </template>
      <template #body>
        <div class="mt-6">
          <div class="mb-8">
            <h2 class="text-base font-medium text-n-slate-12 mb-3">
              Quadros existentes
            </h2>
            <div v-if="boards.length === 0" class="text-n-slate-11">
              Nenhum quadro configurado.
            </div>
            <table
              v-else
              class="min-w-full divide-y divide-n-weak border border-n-weak rounded-lg"
            >
              <thead class="bg-n-solid-1">
                <tr>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-11"
                  >
                    Nome
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-11"
                  >
                    Time
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-11"
                  >
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr v-for="b in boards" :key="b.id">
                  <td class="px-4 py-3">{{ b.name }}</td>
                  <td class="px-4 py-3">{{ getTeamName(b.team_id) }}</td>
                  <td class="px-4 py-3">
                    <div class="flex gap-2">
                      <Button
                        icon="i-lucide-settings"
                        slate
                        xs
                        faded
                        @click="selectForEdit(b)"
                      />
                      <Button
                        icon="i-lucide-trash-2"
                        xs
                        ruby
                        faded
                        @click="removeBoard(b.id)"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <woot-modal
        v-model:show="showCreate"
        :on-close="() => (showCreate = false)"
      >
        <KanbanBoardModal
          mode="create"
          @saved="store.dispatch('boards/getAll')"
          @close="() => (showCreate = false)"
        />
      </woot-modal>

      <woot-modal v-model:show="showEdit" :on-close="() => (showEdit = false)">
        <KanbanBoardModal
          mode="edit"
          :board="selectedBoard"
          @saved="store.dispatch('boards/getAll')"
          @close="() => (showEdit = false)"
        />
      </woot-modal>

      <!-- Keep inline editor area for advanced edits if needed -->
      <template #footer />
    </SettingsLayout>
  </div>
</template>
